import React from 'react'
import * as stores from '../stores/appStore'
import * as types from '../types'

interface GroupModalProps {
  isOpen: boolean
  onClose: () => void
  group?: types.Group
}

const EMOJI_OPTIONS = ['👥', '🏢', '🎯', '⭐', '🚀', '💼', '🏠', '🎨', '📚', '🏫', '🏥', '🏭', '💡', '🌟', '🔥', '💎']

const GroupModal: React.FC<GroupModalProps> = ({ isOpen, onClose, group }) => {
  const { people, addGroup, updateGroup } = stores.useAppStore()
  const [formData, setFormData] = React.useState({
    name: group?.name || '',
    color: group?.color || '#3B82F6',
    emoji: group?.emoji || '👥',
    memberIds: group?.memberIds || []
  })
  const [isLoading, setIsLoading] = React.useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      if (group) {
        await updateGroup(group.id, {
          ...formData
        })
      } else {
        await addGroup({
          ...formData,
          memberIds: formData.memberIds
        })
      }
      onClose()
    } catch (error) {
      console.error('Failed to save group:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const toggleMember = (personId: string) => {
    setFormData(prev => ({
      ...prev,
      memberIds: prev.memberIds.includes(personId)
        ? prev.memberIds.filter(id => id !== personId)
        : [...prev.memberIds, personId]
    }))
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[80vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold">
            {group ? 'Edit Group' : 'Create Group'}
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Group Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Group Name
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Work Team, Family, Friends"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              required
            />
          </div>

          {/* Emoji Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Icon
            </label>
            <div className="grid grid-cols-8 gap-2">
              {EMOJI_OPTIONS.map(emoji => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => setFormData({ ...formData, emoji })}
                  className={`p-2 text-2xl rounded-lg border-2 transition-all ${
                    formData.emoji === emoji
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>

          {/* Color Picker */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Color
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="color"
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                className="h-10 w-20"
              />
              <span className="text-sm text-gray-500">{formData.color}</span>
            </div>
          </div>

          {/* Member Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Members
            </label>
            <div className="space-y-2 max-h-40 overflow-y-auto border rounded-lg p-2">
              {people.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-4">No people added yet</p>
              ) : (
                people.map(person => (
                  <label
                    key={person.id}
                    className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={formData.memberIds.includes(person.id)}
                      onChange={() => toggleMember(person.id)}
                      className="rounded text-primary-600 focus:ring-primary-500"
                    />
                    <div className="flex items-center space-x-2 flex-1">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: person.color }}
                      />
                      <span className="text-sm">
                        {person.firstName} {person.lastName}
                      </span>
                    </div>
                  </label>
                ))
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Selected: {formData.memberIds.length} member{formData.memberIds.length !== 1 && 's'}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
              disabled={isLoading}
            >
              {isLoading ? 'Saving...' : (group ? 'Update Group' : 'Create Group')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default GroupModal